import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UsersRound, ShieldAlert, Undo2, PenSquare, X, Search as SearchIcon } from "lucide-react";

const MODERATION_REASONS = [
  { value: "fake_photo", label: "Fake or stolen photo" },
  { value: "fake_identity", label: "Fake identity" },
  { value: "mismatched_details", label: "Mismatched details" },
  { value: "inappropriate_content", label: "Inappropriate content" },
  { value: "duplicate_account", label: "Duplicate account" },
  { value: "harassment", label: "Harassment or abuse" },
  { value: "other", label: "Other" },
];
const REASON_LABEL = Object.fromEntries(MODERATION_REASONS.map((r) => [r.value, r.label]));

const STATUS_BADGE = {
  active: "bg-emerald-50 text-emerald-800 border-emerald-300",
  under_review: "bg-red-50 text-red-800 border-red-300",
};

const PLAN_LABEL = {
  free: "Free",
  visitor_79: "India Resident £79",
  basic_199: "Connect £199",
  premium_399: "Verified £399",
  elite_699: "Elite £699",
};

const AUDIT_LABEL = {
  mark_invalid: { color: "bg-red-50 text-red-800 border-red-300", label: "Marked invalid" },
  reinstate: { color: "bg-emerald-50 text-emerald-800 border-emerald-300", label: "Reinstated" },
  note: { color: "bg-slate-100 text-slate-700 border-slate-300", label: "Note" },
};

export default function AdminMembers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [plan, setPlan] = useState("all");
  const [memberType, setMemberType] = useState("all");
  const [country, setCountry] = useState("all");
  const [sort, setSort] = useState("recent");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modOpen, setModOpen] = useState(false);
  const [reason, setReason] = useState("fake_photo");
  const [reasonText, setReasonText] = useState("");
  const [shareable, setShareable] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [newNote, setNewNote] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status !== "all") params.set("status", status);
    if (plan !== "all") params.set("plan", plan);
    if (memberType !== "all") params.set("member_type", memberType);
    if (country !== "all") params.set("country", country);
    params.set("sort", sort);
    api.get(`/admin/members?${params.toString()}`)
      .then((r) => setItems(r.data.items))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [q, status, plan, memberType, country, sort]);

  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role, load]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await api.get(`/admin/members/${id}`);
      setDetail(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDetailLoading(false); }
  };

  const submitModerate = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.post(`/admin/members/${detail.profile.id}/moderate`, {
        reason_code: reason,
        reason_text: reasonText,
        shareable,
        note,
      });
      toast.success("Marked under review");
      setModOpen(false);
      setReasonText(""); setShareable(false); setNote("");
      await openDetail(detail.profile.id);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reinstate = async () => {
    if (!detail) return;
    if (!window.confirm(`Reinstate ${detail.profile.full_name}? Their profile becomes visible again.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/members/${detail.profile.id}/reinstate`, { note: "Reviewed and cleared" });
      toast.success("Reinstated");
      await openDetail(detail.profile.id);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitNote = async () => {
    if (!detail || !newNote.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/members/${detail.profile.id}/note`, { note: newNote.trim() });
      toast.success("Note added");
      setNoteOpen(false);
      setNewNote("");
      await openDetail(detail.profile.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    const c = { active: 0, under_review: 0 };
    items.forEach((r) => { c[r.moderation_status] = (c[r.moderation_status] || 0) + 1; });
    return c;
  }, [items]);

  if (user && user.role !== "admin") {
    return <div className="max-w-3xl mx-auto p-10"><Card className="p-8 text-center">Admin only.</Card></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 space-y-6" data-testid="admin-members-page">
      <div className="flex items-center gap-3">
        <UsersRound className="w-8 h-8 text-[#6B1D2F]" />
        <div>
          <h1 className="font-serif text-3xl font-semibold text-slate-900">Members</h1>
          <p className="text-slate-600">Moderate profiles, review reports, and manage account status.</p>
        </div>
      </div>

      <Card className="p-4 border-[#EAE3D2] bg-white">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="members-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, email or mobile…"
              className="pl-9"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="members-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="under_review">Under review</SelectItem>
            </SelectContent>
          </Select>
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger data-testid="members-plan-filter"><SelectValue placeholder="Plan" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="visitor_79">India Resident £79</SelectItem>
              <SelectItem value="basic_199">Connect £199</SelectItem>
              <SelectItem value="premium_399">Verified £399</SelectItem>
              <SelectItem value="elite_699">Elite £699</SelectItem>
            </SelectContent>
          </Select>
          <Select value={memberType} onValueChange={setMemberType}>
            <SelectTrigger data-testid="members-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="nri">NRI</SelectItem>
              <SelectItem value="india">India Resident</SelectItem>
            </SelectContent>
          </Select>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid="members-country-filter"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All countries</SelectItem>
              <SelectItem value="United Kingdom">United Kingdom</SelectItem>
              <SelectItem value="Australia">Australia</SelectItem>
              <SelectItem value="India">India</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-slate-600">
          <span>Sort:</span>
          <Button variant={sort === "recent" ? "default" : "outline"} className={sort === "recent" ? "bg-[#6B1D2F] hover:bg-[#541624] text-white h-7 px-3 rounded-full" : "h-7 px-3 rounded-full"} onClick={() => setSort("recent")} data-testid="sort-recent">Recently registered</Button>
          <Button variant={sort === "active" ? "default" : "outline"} className={sort === "active" ? "bg-[#6B1D2F] hover:bg-[#541624] text-white h-7 px-3 rounded-full" : "h-7 px-3 rounded-full"} onClick={() => setSort("active")} data-testid="sort-active">Last active</Button>
          <span className="ml-auto">
            <Badge className="bg-emerald-50 text-emerald-800 border-emerald-300 mr-2">Active {counts.active || 0}</Badge>
            <Badge className="bg-red-50 text-red-800 border-red-300">Under review {counts.under_review || 0}</Badge>
          </span>
        </div>
      </Card>

      <Card className="border-[#EAE3D2] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6F0] text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="text-left px-3 py-3">Member</th>
                <th className="text-left px-3 py-3">Type / Plan</th>
                <th className="text-left px-3 py-3">Location</th>
                <th className="text-left px-3 py-3">Verification</th>
                <th className="text-left px-3 py-3">Moderation</th>
                <th className="text-left px-3 py-3">Registered</th>
                <th className="text-left px-3 py-3">Reports</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center p-8 text-slate-500">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="text-center p-8 text-slate-500" data-testid="members-empty">No members match.</td></tr>
              )}
              {items.map((m) => (
                <tr key={m.id} className="border-t border-[#EAE3D2] hover:bg-[#FAF6F0] cursor-pointer" data-testid={`member-row-${m.id}`} onClick={() => openDetail(m.id)}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#6B1D2F] text-white flex items-center justify-center font-serif overflow-hidden shrink-0">
                        {m.profile_photo_path
                          ? <img src={fileUrl(m.profile_photo_path)} alt="" className="w-full h-full object-cover" />
                          : (m.full_name?.[0] || "?")}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{m.full_name || "—"}</p>
                        <p className="text-xs text-slate-500 truncate">{m.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    <div>{m.member_type === "india" ? "India Resident" : "NRI"} · {m.gender || "—"} · {m.age || "—"}</div>
                    <div className="text-xs text-slate-500">{PLAN_LABEL[m.plan] || m.plan || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{m.current_city || "—"}<div className="text-xs text-slate-500">{m.residence_country || "—"}</div></td>
                  <td className="px-3 py-2"><Badge className="bg-slate-100 text-slate-700 border-slate-300">{m.verification_status || "—"}</Badge></td>
                  <td className="px-3 py-2"><Badge className={STATUS_BADGE[m.moderation_status]}>{m.moderation_status === "under_review" ? "Under review" : "Active"}</Badge></td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}<div className="text-slate-400">seen {m.last_seen_at ? new Date(m.last_seen_at).toLocaleDateString() : "—"}</div></td>
                  <td className="px-3 py-2">{m.open_reports > 0 ? <Badge className="bg-amber-50 text-amber-800 border-amber-300">{m.open_reports} open</Badge> : <span className="text-slate-400 text-xs">—</span>}</td>
                  <td className="px-3 py-2 text-right"><Button variant="outline" size="sm" className="rounded-full h-7" onClick={(e) => { e.stopPropagation(); openDetail(m.id); }}>Review</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selectedId} onOpenChange={(o) => { if (!o) { setSelectedId(null); setDetail(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="member-detail-dialog">
          {detailLoading || !detail ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#6B1D2F] text-white flex items-center justify-center font-serif overflow-hidden">
                      {detail.profile.profile_photo_path
                        ? <img src={fileUrl(detail.profile.profile_photo_path)} alt="" className="w-full h-full object-cover" />
                        : (detail.profile.full_name?.[0] || "?")}
                    </div>
                    <div>
                      <div className="font-serif">{detail.profile.full_name}</div>
                      <div className="text-xs text-slate-500 font-sans">{detail.profile.email} · {detail.profile.mobile}</div>
                    </div>
                    <Badge className={`ml-3 ${STATUS_BADGE[detail.profile.moderation_status || "active"]}`}>
                      {detail.profile.moderation_status === "under_review" ? "Under review" : "Active"}
                    </Badge>
                  </div>
                </DialogTitle>
                <DialogDescription>
                  Registered {detail.profile.created_at ? new Date(detail.profile.created_at).toLocaleString() : "—"} · Last seen {detail.profile.last_seen_at ? new Date(detail.profile.last_seen_at).toLocaleString() : "—"}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="text-xs uppercase tracking-wider text-slate-500">Profile</h4>
                  <Field k="Age / Gender" v={`${detail.profile.age || "—"} · ${detail.profile.gender || "—"}`} />
                  <Field k="Type / Plan" v={`${detail.profile.member_type || "nri"} · ${PLAN_LABEL[detail.profile.plan] || detail.profile.plan || "free"}`} />
                  <Field k="Community" v={detail.profile.caste || "—"} />
                  <Field k="Profession" v={`${detail.profile.profession || "—"} · ${detail.profile.company || "—"}`} />
                  <Field k="Visa" v={`${detail.profile.visa_type || "—"} · exp ${detail.profile.visa_expiry_date || "—"}`} />
                  <Field k="Location" v={`${detail.profile.current_city || "—"}, ${detail.profile.residence_country || "—"}`} />
                  <Field k="Home country" v={`${detail.profile.home_country_city || "—"}, ${detail.profile.home_country || "—"}`} />
                  <Field k="Family" v={`${detail.profile.father_name || "—"} / ${detail.profile.mother_name || "—"}`} />
                  {detail.profile.about_me && <Field k="About" v={detail.profile.about_me} />}
                </div>
                <div className="space-y-3">
                  <h4 className="text-xs uppercase tracking-wider text-slate-500">Photos & Media</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {detail.profile.profile_photo_path && (
                      <a href={fileUrl(detail.profile.profile_photo_path)} target="_blank" rel="noreferrer" className="block">
                        <img src={fileUrl(detail.profile.profile_photo_path)} alt="profile" className="w-full h-24 object-cover rounded border border-[#EAE3D2]" />
                        <p className="text-[10px] text-slate-500 mt-1 text-center">Profile</p>
                      </a>
                    )}
                    {(detail.profile.gallery_paths || []).map((p) => (
                      <a href={fileUrl(p)} target="_blank" rel="noreferrer" key={p} className="block">
                        <img src={fileUrl(p)} alt="g" className="w-full h-24 object-cover rounded border border-[#EAE3D2]" />
                      </a>
                    ))}
                    {detail.profile.verification_selfie_path && (
                      <a href={fileUrl(detail.profile.verification_selfie_path)} target="_blank" rel="noreferrer" className="block">
                        <img src={fileUrl(detail.profile.verification_selfie_path)} alt="selfie" className="w-full h-24 object-cover rounded border border-amber-300" />
                        <p className="text-[10px] text-amber-800 mt-1 text-center">Selfie</p>
                      </a>
                    )}
                    {detail.profile.video_intro_path && (
                      <a href={fileUrl(detail.profile.video_intro_path)} target="_blank" rel="noreferrer" className="block col-span-3 text-xs text-[#6B1D2F] underline">
                        Video intro (open)
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-[#EAE3D2]">
                {detail.profile.moderation_status === "under_review" ? (
                  <Button data-testid="reinstate-btn" disabled={busy} onClick={reinstate} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
                    <Undo2 className="w-4 h-4 mr-1" />Reinstate to Active
                  </Button>
                ) : (
                  <Button data-testid="mark-invalid-btn" disabled={busy} onClick={() => setModOpen(true)} className="bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full">
                    <ShieldAlert className="w-4 h-4 mr-1" />Mark Invalid
                  </Button>
                )}
                <Button data-testid="add-note-btn" disabled={busy} onClick={() => setNoteOpen(true)} variant="outline" className="rounded-full">
                  <PenSquare className="w-4 h-4 mr-1" />Add internal note
                </Button>
              </div>

              {/* Reports */}
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Reports received ({detail.reports.length})</h4>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {detail.reports.length === 0 && <p className="text-sm text-slate-400">No reports.</p>}
                  {detail.reports.map((r) => (
                    <div key={r.id} className="p-2 rounded border border-[#EAE3D2] bg-[#FAF6F0] text-xs">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-[#6B1D2F]/10 text-[#6B1D2F] border-[#6B1D2F]/30">{r.reason}</Badge>
                        <Badge className={r.status === "open" ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-100 text-slate-700 border-slate-300"}>{r.status}</Badge>
                        <span className="text-slate-500">by {r.reporter?.full_name || r.reporter_id?.slice(0, 8)}</span>
                        <span className="text-slate-400 ml-auto">{new Date(r.created_at).toLocaleString()}</span>
                      </div>
                      {r.note && <p className="mt-1 text-slate-700 whitespace-pre-wrap">{r.note}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Audit */}
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Admin actions ({detail.audit.length})</h4>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {detail.audit.length === 0 && <p className="text-sm text-slate-400">No prior actions.</p>}
                  {detail.audit.map((a) => {
                    const meta = AUDIT_LABEL[a.action] || { color: "bg-slate-100 text-slate-700 border-slate-300", label: a.action };
                    return (
                      <div key={a.id} className="p-2 rounded border border-[#EAE3D2] text-xs">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={meta.color}>{meta.label}</Badge>
                          {a.reason_code && <span className="text-slate-600">{REASON_LABEL[a.reason_code] || a.reason_code}</span>}
                          {a.shareable && <Badge className="bg-blue-50 text-blue-800 border-blue-300">Shared with owner</Badge>}
                          <span className="text-slate-500 ml-auto">by {a.admin_name}</span>
                          <span className="text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                        </div>
                        {a.reason_text && <p className="mt-1 text-slate-700 whitespace-pre-wrap"><b>Reason:</b> {a.reason_text}</p>}
                        {a.note && <p className="mt-1 text-slate-700 whitespace-pre-wrap"><b>Note:</b> {a.note}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Visa checks */}
              {detail.visa_checks.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Visa verification history ({detail.visa_checks.length})</h4>
                  <div className="space-y-1 text-xs">
                    {detail.visa_checks.map((v, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Badge className="bg-slate-100 text-slate-700 border-slate-300">{v.service || v.result?.outcome || "check"}</Badge>
                        <span className="text-slate-500">{v.checked_at ? new Date(v.checked_at).toLocaleString() : "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Moderate dialog */}
      <Dialog open={modOpen} onOpenChange={setModOpen}>
        <DialogContent data-testid="moderate-dialog">
          <DialogHeader>
            <DialogTitle>Mark profile invalid</DialogTitle>
            <DialogDescription>The member will be signed out, hidden from search and chats, and receive an email notification. Any pending auto-renew is cancelled.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="moderate-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODERATION_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Reason detail (optional)</label>
              <Textarea data-testid="moderate-reason-text" rows={2} value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Give the moderator context…" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Internal note (optional)</label>
              <Textarea data-testid="moderate-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Admins-only notes…" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="moderate-shareable" checked={shareable} onChange={(e) => setShareable(e.target.checked)} />
              <span>Include reason in the email to the member</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModOpen(false)}><X className="w-4 h-4 mr-1" />Cancel</Button>
            <Button data-testid="moderate-confirm" disabled={busy} onClick={submitModerate} className="bg-[#6B1D2F] hover:bg-[#541624] text-white">
              {busy ? "Saving…" : "Confirm mark invalid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent data-testid="add-note-dialog">
          <DialogHeader>
            <DialogTitle>Add internal note</DialogTitle>
            <DialogDescription>Admin-only note attached to this member's audit trail.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="add-note-input" rows={4} value={newNote} onChange={(e) => setNewNote(e.target.value)} maxLength={2000} placeholder="Type your note…" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button data-testid="add-note-confirm" disabled={busy || !newNote.trim()} onClick={submitNote} className="bg-[#6B1D2F] hover:bg-[#541624] text-white">Save note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wider text-slate-500">{k}</span>
      <span className="text-slate-800 text-sm">{v || "—"}</span>
    </div>
  );
}
